/**
 * Splash — full-screen brand sequence.
 *
 * Multi-phase choreography over ~2.4 s, all driven by the shared
 * :class:`DysonSphere` component (changing it once changes the splash,
 * the sidebar logo, the voice orb — everywhere).
 *
 *   ┌────────────┬──────────────────────────────────────────────┐
 *   │ phase      │ what happens                                 │
 *   ├────────────┼──────────────────────────────────────────────┤
 *   │ ignite     │ sphere appears tiny + faint; rings static    │
 *   │ (0-200ms)  │                                              │
 *   ├────────────┼──────────────────────────────────────────────┤
 *   │ spin       │ rings begin orbit at moderate speed; sun     │
 *   │ (200-1100) │ starts to glow                               │
 *   ├────────────┼──────────────────────────────────────────────┤
 *   │ bloom      │ sphere scales to full size, rings spin at    │
 *   │ (1100-1900)│ max, sun blazes, glow halo expands           │
 *   ├────────────┼──────────────────────────────────────────────┤
 *   │ open       │ sphere stays; wordmark and tagline fade in   │
 *   │ (1900-2200)│                                              │
 *   ├────────────┼──────────────────────────────────────────────┤
 *   │ exit       │ everything fades out; app shell takes over   │
 *   │ (2200-2400)│                                              │
 *   └────────────┴──────────────────────────────────────────────┘
 *
 * Respects ``prefers-reduced-motion``: collapses the whole sequence
 * to a single fade-in (~200 ms) so users with vestibular sensitivity
 * don't get the bloom.
 */
import * as React from "react";
import { cn } from "@/lib/cn";
import { DysonSphere } from "@/components/ui/DysonSphere";
import { Wordmark } from "./Wordmark";

interface SplashProps {
  onComplete: () => void;
}

type Phase = "ignite" | "spin" | "bloom" | "open" | "exit";

const DURATIONS: Record<Phase, number> = {
  ignite: 200,
  spin: 900,
  bloom: 800,
  open: 300,
  exit: 200,
};

const TOTAL = Object.values(DURATIONS).reduce((a, b) => a + b, 0);

export function Splash({ onComplete }: SplashProps) {
  const [phase, setPhase] = React.useState<Phase>("ignite");

  const prefersReducedMotion = React.useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  React.useEffect(() => {
    if (prefersReducedMotion) {
      const t = setTimeout(onComplete, 200);
      return () => clearTimeout(t);
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    let acc = 0;
    const phases: Phase[] = ["spin", "bloom", "open", "exit"];
    for (const p of phases) {
      acc += DURATIONS[
        p === "spin" ? "ignite" : p === "bloom" ? "spin" : p === "open" ? "bloom" : "open"
      ];
      const captured = p;
      timers.push(setTimeout(() => setPhase(captured), acc));
    }
    timers.push(setTimeout(onComplete, TOTAL));
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [onComplete, prefersReducedMotion]);

  // Per-phase visual configuration. The sphere stays mounted through
  // every phase so the rings keep their phase angle — no jumps.
  const sphereMode = (() => {
    if (prefersReducedMotion) return "idle" as const;
    if (phase === "ignite") return "thinking" as const; // slow warm-up
    if (phase === "spin") return "listening" as const; // medium speed
    return "splash" as const; // bloom, open, exit — max intensity
  })();

  const sphereScale = (() => {
    if (prefersReducedMotion) return 1;
    if (phase === "ignite") return 0.55;
    if (phase === "spin") return 0.85;
    return 1; // bloom / open / exit
  })();

  const sphereOpacity = (() => {
    if (prefersReducedMotion) return 1;
    if (phase === "ignite") return 0.4;
    return 1;
  })();

  const glow = (() => {
    if (prefersReducedMotion) return 0;
    if (phase === "ignite") return 0;
    if (phase === "spin") return 0.4;
    if (phase === "bloom") return 1;
    if (phase === "open") return 0.85;
    return 0.6;
  })();

  const wordmarkVisible = phase === "open" || phase === "exit" || prefersReducedMotion;
  const exiting = phase === "exit";

  return (
    <div
      role="status"
      aria-label="Loading Omni"
      className={cn(
        "fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-bg",
        "transition-opacity duration-200 ease-out",
        exiting ? "opacity-0" : "opacity-100",
      )}
    >
      {/* Radial backdrop gradient — subtle warm focal point behind the sphere. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 45%, color-mix(in oklch, var(--om-color-warning) 14%, transparent) 0%, transparent 70%)",
          opacity: prefersReducedMotion ? 0.6 : glow,
          transition: "opacity 600ms ease-out",
        }}
      />

      <div
        className="relative"
        style={{
          transform: `scale(${sphereScale})`,
          opacity: sphereOpacity,
          transition: prefersReducedMotion
            ? "none"
            : "transform 600ms cubic-bezier(0.16, 1, 0.3, 1), opacity 400ms ease-out",
        }}
      >
        <DysonSphere mode={sphereMode} glow={glow} className="h-44 w-44" />
      </div>

      <div
        className={cn(
          "mt-6 flex flex-col items-center transition-all duration-500 ease-out",
          wordmarkVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-2",
        )}
      >
        <Wordmark size="lg" />
        <span className="mt-1 text-xs font-mono uppercase tracking-[0.3em] text-fg-faint">
          One brain · Every device · Always on
        </span>
      </div>
    </div>
  );
}
